import React from "react";
import styled from "styled-components";
import { Icon } from "./Icon";
import { copy } from "./constants/copy";
import { style } from "./style";
import { SCHEMA, SCHEMAS, TESTS } from "./utils/StringValidation";

const RequirementsMeter = styled.div`
  background-color: ${style.colors.grey.light};
  height: 8px;
  margin-bottom: ${style.vars.whitespace.xsmall};
  margin-top: ${style.vars.whitespace.regular};
`;

type IndicatorProps = {
  color: string;
  width: number;
};

const MeterIndicator = styled.div<IndicatorProps>`
  background-color: ${(props) => props.color};
  height: 8px;
  width: ${(props) => props.width}%;
  transition: width ${style.vars.transitionSpeed.base} ${style.vars.easeInOutCubic};
`;

const Subheading = styled.label`
  font-size: ${style.vars.fontSize.base};
  letter-spacing: inherit;
  text-transform: inherit;
`;

const PasswordRequirements = styled.ul`
  list-style: none;
  margin: 0;
  padding-left: 0;
`;

const RequirementItem = styled.li`
  color: ${style.colors.grey.darkest};
  font-size: ${style.vars.fontSize.base};
`;

const RequirementIcon = styled(Icon)`
  height: 12px;
  margin-right: ${style.vars.whitespace.small};
  margin-left: ${style.vars.whitespace.xsmall};
`;

const SatisfiedIcon = styled(RequirementIcon)`
  color: ${style.colors.green.primary};
`;

const UnsatisfiedIcon = styled(RequirementIcon)`
  color: ${style.colors.red.primary};
`;

/**
 * This utility function returns the color of the indicator,
 * with a progression of gray -> red -> orange -> green.
 *
 * @param width - numeric value between 0 and 100 inclusive
 */
const meterColor = (width: number) => {
  if (width < 50) {
    return style.colors.red.dark;
  } else if (width < 100) {
    return style.colors.orange.primary;
  } else {
    return style.colors.green.primary;
  }
};

/**
 * This utility function returns true if the provided password
 * adheres to each rule in the predefined schema.
 *
 * This is not used by this component, but rather for Yup validation.
 *
 * @param password - password to be evaluated
 * @param schema - enum indicating a predefined schema
 */
export const evaluatePassword = (password: string, schema: SCHEMA) => {
  return SCHEMAS[schema].every((test) => {
    return TESTS[test].evaluate(password);
  });
};

type Props = {
  name?: string;
  value: string;
  schema?: SCHEMA;
};

export const PasswordMeter: React.FC<Props> = ({ value, schema = SCHEMA.PASSWORD_DEFAULT }) => {
  let width = 0;
  const increment = 100 / SCHEMAS[schema].length;

  const requirements = SCHEMAS[schema].map((requirement, i) => {
    const isSatisfied = TESTS[requirement].evaluate(value);
    if (isSatisfied && value) {
      width += increment;
    }
    return (
      <RequirementItem key={i}>
        {value && isSatisfied ? <SatisfiedIcon icon="check" /> : <UnsatisfiedIcon icon="close" />}
        {TESTS[requirement].text}
      </RequirementItem>
    );
  });

  return (
    <>
      <RequirementsMeter>
        <MeterIndicator color={meterColor(width)} width={width} />
      </RequirementsMeter>
      <Subheading>{copy.PasswordMeter.passwordRequirementSubheading}</Subheading>
      <PasswordRequirements>{requirements}</PasswordRequirements>
    </>
  );
};
